// HubSpotAttributionFrame — AA "HubSpot | Paid Search" / "HubSpot | Paid
// Social" report page parity. Sessions/contacts attribution sourced from
// AA's hub-spot `channel-analytics` AAQL asset (read_client_data_source) —
// see backend HubSpotChannelSection docstring for why read_client_traffic's
// documented hub-spot entityType enum (channel/referrer/social_network/
// page/location) cannot reach this data (no campaign dimension, and its
// social_network entityType resolves to the ORGANIC "social" channel, not
// "paid-social").
//
// One block per paid channel (Paid Search, Paid Social): a sessions-trend
// chart + a campaign/network donut side by side, then a KPI strip, then a
// detail table — matching the reference "HubSpot | Paid Search" / "HubSpot
// | Paid Social" screenshots field-for-field (live-verified against ALIBI
// Music, 2026-08-09). Adaptive: the whole frame renders nothing when
// neither channel has data; each channel block independently hides when
// its own channel has none (Precision Restorations' paid_social and Tyler
// McBroom's paid_search are both real but small — both still render); the
// trend chart independently hides when its own `over_time` has fewer than
// 2 points.
//
// Trend chart: `over_time` is DAILY (per-day HubSpot channel-analytics
// pull, matching AA's own reference chart granularity — evidence-audit GAP
// 1, closed), and each point's optional `prior_value` (day-offset-matched
// against a real second pull, never fabricated — see TrendPoint's own
// docstring) drives a paler "Previous Period" overlay line automatically.
// Rendered via the shared TrendChartCard primitive (primitives.tsx) — the
// same one TrafficPanel/MetaAdsPanel already use for their own daily
// current-vs-previous trend cards — rather than a bespoke chart here, so
// axis ticks, tooltip, legend and overlay styling stay consistent with the
// rest of the dashboard. A channel whose `over_time` hasn't been backfilled
// yet (fewer than 2 points) just hides the trend card and still renders its
// donut/KPI/table, same adaptive-hide philosophy as the paragraph above.
//
// Sessions KPI badge: ChangePctBadge fed by data.comparison.
// sessions_change_pct (HubSpotPeriodComparison), NOT TrendBadge off
// `over_time`. `over_time` going daily above means TrendBadge's
// last-two-points diff would read as a day-over-day change rendered right
// beside the KPI strip -- easy to misread as the period change, exactly
// the bug AdvertisingPanel/LocalPanel already hit and fixed for their own
// daily series (see backend HubSpotPeriodComparison's docstring). Renders
// no badge at all when data.comparison is absent, never a fallback to the
// day-over-day number.
//
// Self-contained: recharts + named theme tokens only. Donut palette is an
// 8-step sequential ramp between the two brand anchors --color-coral
// (primary) and --color-teal-dark (secondary-dark), built with color-mix
// (the same technique components/dashboard/TrendChart.tsx already uses for
// its area fills) rather than reaching for unrelated hues — AA's own
// reference donuts are single-hue sequential ramps too, ordered by
// magnitude, not a categorical rainbow. Overflow beyond 8 folds into a
// muted "Other" slice.

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { HubSpotAttributionSection, HubSpotChannelSection } from "@/types/clientAnalytics";
import {
  KpiStrip,
  StatBlock,
  SourceChip,
  NotConnected,
  TrendChartCard,
  ChangePctBadge,
  formatNum,
} from "@/components/client-analytics/primitives";

const DONUT_PALETTE = [
  "var(--color-coral)",
  "color-mix(in srgb, var(--color-coral) 86%, var(--color-teal-dark))",
  "color-mix(in srgb, var(--color-coral) 71%, var(--color-teal-dark))",
  "color-mix(in srgb, var(--color-coral) 57%, var(--color-teal-dark))",
  "color-mix(in srgb, var(--color-coral) 43%, var(--color-teal-dark))",
  "color-mix(in srgb, var(--color-coral) 29%, var(--color-teal-dark))",
  "color-mix(in srgb, var(--color-coral) 14%, var(--color-teal-dark))",
  "var(--color-teal-dark)",
];
const OTHER_COLOR = "var(--color-muted-foreground)";
const MAX_DONUT_SLICES = DONUT_PALETTE.length;

function formatSeconds(seconds?: number | null): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function ChannelDonut({ items, unit }: { items: { label: string; value: number }[]; unit: string }) {
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const named = sorted.slice(0, MAX_DONUT_SLICES).map((it, i) => ({
    name: it.label,
    value: it.value,
    color: DONUT_PALETTE[i],
  }));
  const restTotal = sorted.slice(MAX_DONUT_SLICES).reduce((sum, it) => sum + it.value, 0);
  const slices = restTotal > 0 ? [...named, { name: "Other", value: restTotal, color: OTHER_COLOR }] : named;
  const total = slices.reduce((s, x) => s + x.value, 0);

  if (total <= 0) {
    return (
      <div className="flex items-center justify-center text-dash-context text-muted-foreground" style={{ height: 180 }}>
        No breakdown data
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4" style={{ height: 180 }}>
      <div className="relative h-full w-[150px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={72}
              paddingAngle={2}
              stroke="var(--color-warm-bg)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((s) => (
                <Cell key={s.name} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid var(--color-border)", fontSize: 12 }}
              formatter={(value: number, name) => [
                `${value.toLocaleString()} ${unit} (${total > 0 ? Math.round((value / total) * 100) : 0}%)`,
                name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="tabular text-dash-value font-semibold text-foreground">{total.toLocaleString()}</span>
          <span className="text-dash-context text-muted-foreground">{unit}</span>
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-1.5 max-h-[180px] overflow-y-auto">
        {slices.map((s) => (
          <li key={s.name} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
            <span className="truncate text-dash-context text-foreground" title={s.name}>
              {s.name}
            </span>
            <span className="tabular ml-auto shrink-0 text-dash-context text-muted-foreground">
              {s.value.toLocaleString()} · {total > 0 ? Math.round((s.value / total) * 100) : 0}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChannelBlock({
  title,
  columnLabel,
  data,
}: {
  title: string;
  columnLabel: string;
  data: HubSpotChannelSection;
}) {
  const rows = [...(data.rows ?? [])].sort((a, b) => b.sessions - a.sessions);
  // Adaptive: fewer than 2 raw points isn't a trend, it's a dot — hide the
  // whole trend card rather than render an empty/misleading axis (matches
  // the frame's existing per-channel adaptive-hide philosophy above).
  const hasTrend = (data.over_time?.length ?? 0) >= 2;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="text-dash-subtitle font-semibold text-foreground">{title}</div>
        <SourceChip label="HubSpot" />
      </div>
      <div className={hasTrend ? "flex flex-col gap-4 lg:flex-row lg:items-stretch" : ""}>
        {hasTrend && (
          <div className="flex-1 min-w-0">
            {/* prior_value on each over_time point drives the paler
                "Previous Period" overlay automatically — see
                TrendChartCard's own doc comment. Badge is the REAL
                period-over-period Sessions delta (data.comparison), not a
                TrendBadge off over_time — over_time is DAILY (91 points),
                so TrendBadge's last-two-points diff would silently read as
                a day-over-day change next to the KPI strip below, the exact
                bug AdvertisingPanel/LocalPanel already hit and fixed for
                their own daily series (see HubSpotPeriodComparison's doc
                comment). Renders no badge when the prior-period trend pull
                hasn't populated data.comparison yet — never falls back to
                TrendBadge's day-over-day read. */}
            <TrendChartCard
              title="Sessions"
              value={formatNum(data.sessions)}
              badge={<ChangePctBadge pct={data.comparison?.sessions_change_pct} />}
              seriesName="Sessions"
              points={data.over_time}
            />
          </div>
        )}
        <div className={hasTrend ? "flex-1 min-w-0 rounded-lg border border-border bg-card p-4" : "rounded-lg border border-border bg-card p-4"}>
          <ChannelDonut items={data.by_breakdown ?? []} unit="sessions" />
        </div>
      </div>
      <KpiStrip>
        <StatBlock label="Sessions" value={formatNum(data.sessions)} hero />
        <StatBlock label="New Visitor Sessions" value={formatNum(data.new_visitor_sessions)} />
        <StatBlock label="New Contacts" value={formatNum(data.new_contacts)} />
        <StatBlock label="Avg. Session Length" value={formatSeconds(data.avg_session_length)} />
      </KpiStrip>
      {rows.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-dash-context">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-3 py-2 font-semibold uppercase tracking-wide">{columnLabel}</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wide text-right">Sessions</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wide text-right">New Visitor</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wide text-right">New Contacts</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wide text-right">Bounce Rate</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wide text-right">PV / Session</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wide text-right">Avg. Session</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 truncate max-w-[220px] text-foreground" title={r.label}>
                    {r.label}
                  </td>
                  <td className="px-3 py-2 text-right tabular text-foreground">{formatNum(r.sessions)}</td>
                  <td className="px-3 py-2 text-right tabular text-muted-foreground">
                    {formatNum(r.new_visitor_sessions)}
                  </td>
                  <td className="px-3 py-2 text-right tabular text-muted-foreground">{formatNum(r.new_contacts)}</td>
                  <td className="px-3 py-2 text-right tabular text-muted-foreground">
                    {r.bounce_rate != null ? `${r.bounce_rate.toFixed(1)}%` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular text-muted-foreground">
                    {r.pageviews_per_session != null ? r.pageviews_per_session.toFixed(2) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular text-muted-foreground">
                    {formatSeconds(r.avg_session_length)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function HubSpotAttributionFrame({
  data,
  periodEnd,
}: {
  data?: HubSpotAttributionSection | null;
  periodEnd?: string | null;
}) {
  if (!data || (!data.paid_search && !data.paid_social)) {
    return (
      <NotConnected
        message="HubSpot paid attribution — no paid-search/paid-social HubSpot traffic connected"
        cta="Talk to your account manager"
      />
    );
  }
  return (
    <div className="flex flex-col gap-8">
      {data.paid_search && (
        <ChannelBlock title="Paid Search" columnLabel="Paid Campaign" data={data.paid_search} />
      )}
      {data.paid_social && (
        <ChannelBlock title="Paid Social" columnLabel="Paid Social Network" data={data.paid_social} />
      )}
    </div>
  );
}
